import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Severity, Skel } from '../components/ui'
import { thaiDateTime } from '../lib/format'

type GuardSeverity = 'low' | 'medium' | 'high'

interface GuardFlag {
  id: string
  document_id: string
  code: string
  severity: GuardSeverity
  offset: number
  excerpt: string
  detail: string
  created_at?: string
}

interface GuardCodeCount {
  code: string
  severity: GuardSeverity
  count: number
  documents: number
}

interface GuardDocument {
  document_id: string
  original_name: string
  kind: string
  status: string
  flag_count: number
  max_severity: GuardSeverity
  last_flagged_at?: string
  flags: GuardFlag[]
}

interface GuardSummary {
  total_flags: number
  flagged_documents: number
  codes: GuardCodeCount[]
  documents: GuardDocument[]
}

const CODE_ORDER = [
  'PROMPT_INJECTION',
  'HIDDEN_INSTRUCTION',
  'PII_LEAK',
  'SUSPICIOUS_AMOUNT',
  'DUPLICATE_TEMPLATE',
] as const

const codeMeta: Record<string, { label: string; desc: string }> = {
  PROMPT_INJECTION: {
    label: 'คำสั่งแอบแฝง',
    desc: 'ข้อความที่พยายามสั่งให้ระบบเปลี่ยนหน้าที่หรือเปิดเผยคำสั่งภายใน',
  },
  HIDDEN_INSTRUCTION: {
    label: 'คำสั่งซ่อนรูป',
    desc: 'อักขระ zero-width, สีขาวบนพื้นขาว หรือข้อมูล base64 ขนาดใหญ่',
  },
  PII_LEAK: {
    label: 'ข้อมูลส่วนบุคคล',
    desc: 'เลขประจำตัวประชาชน บัตรชำระเงิน หรืออีเมล — แสดงแบบปกปิด',
  },
  SUSPICIOUS_AMOUNT: {
    label: 'จำนวนเงินผิดปกติ',
    desc: 'ตัวคั่นผสมหรือทศนิยมตั้งแต่สามตำแหน่ง ซึ่งไม่ใช่รูปแบบสกุลเงิน',
  },
  DUPLICATE_TEMPLATE: {
    label: 'เทมเพลตซ้ำ',
    desc: 'ความคล้ายของชุดคำตั้งแต่ 92% ขึ้นไปเมื่อเทียบกับเอกสารในคลัง',
  },
}

const kindLabel: Record<string, string> = {
  invoice: 'ใบแจ้งหนี้',
  receipt: 'ใบเสร็จ',
  tax_invoice: 'ใบกำกับภาษี',
  bank_statement: 'รายการเดินบัญชี',
  contract: 'สัญญา',
  payroll: 'เงินเดือน',
  ledger_export: 'ส่งออกบัญชี',
  purchase_order: 'ใบสั่งซื้อ',
  delivery_note: 'ใบส่งของ',
  other: 'อื่น ๆ',
}

const statusLabel: Record<string, string> = {
  uploaded: 'รออัปโหลด',
  processing: 'กำลังประมวลผล',
  ready: 'พร้อมใช้',
  failed: 'ล้มเหลว',
  quarantined: 'กักกัน',
  archived: 'เก็บถาวร',
}

/**
 * The API already masks PII, but the page masks again before rendering: a reviewer's
 * screen must never become the place where a full national id or email is copied.
 */
function maskSensitive(input: string): string {
  let out = input.replace(/\b\d{13}\b/g, (m) => `${m.slice(0, 3)}${'x'.repeat(8)}${m.slice(-2)}`)
  out = out.replace(/\b(?:\d{4}[ -]?){3}\d{4}\b/g, (m) => {
    const digits = m.replace(/\D/g, '')
    return `•••• •••• •••• ${digits.slice(-4)}`
  })
  out = out.replace(/[A-Za-z0-9._%+-]+@([A-Za-z0-9.-]+\.[A-Za-z]{2,})/g, (m, domain: string) => {
    const local = m.slice(0, Math.max(0, m.indexOf('@')))
    return `${local.slice(0, 2)}•••@${domain}`
  })
  return out
}

export function Guardrails() {
  const qc = useQueryClient()
  const [openId, setOpenId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const summary = useQuery({
    queryKey: ['guardrails'],
    queryFn: () => api<GuardSummary>('/v1/guardrails/summary'),
    staleTime: 15_000,
  })

  const rescan = useMutation({
    mutationFn: (documentId: string) =>
      apiWrite<{ count: number }>(`/v1/documents/${documentId}/guard`, 'POST', {}),
    onSuccess: (res) => {
      setNotice(`ตรวจใหม่แล้ว พบ ${res.count} รายการ`)
      void qc.invalidateQueries({ queryKey: ['guardrails'] })
    },
    onError: (e) => setNotice(e instanceof Error ? e.message : 'ตรวจใหม่ไม่สำเร็จ'),
  })

  const data = summary.data
  const rows = data?.documents ?? []
  const counts = new Map<string, GuardCodeCount>(
    (data?.codes ?? []).map((c): [string, GuardCodeCount] => [c.code, c]),
  )

  return (
    <>
      <PageHead
        title="การป้องกันเอกสารขาเข้า"
        sub={`${data?.total_flags ?? 0} รายการจาก ${data?.flagged_documents ?? 0} เอกสาร · ตรวจด้วยกฎที่อธิบายได้ ไม่เรียก LLM ภายนอก`}
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(188px, 1fr))',
          gap: 12,
          marginBottom: 20,
        }}
      >
        {CODE_ORDER.map((code) => {
          const meta = codeMeta[code]
          const c = counts.get(code)
          return (
            <div key={code} className="card card-pad rise">
              <div className="card-label">{meta.label}</div>
              <div className="stat-value">
                {c?.count ?? 0}
                <span className="stat-unit">รายการ</span>
              </div>
              <div className="stat-foot" style={{ lineHeight: 1.55 }}>{meta.desc}</div>
              <div className="dim" style={{ fontSize: 11, marginTop: 8 }}>
                {c?.documents ?? 0} เอกสาร · โค้ด {code}
              </div>
            </div>
          )
        })}
      </div>

      {notice ? (
        <div className="dim" style={{ fontSize: 12, marginBottom: 12 }}>{notice}</div>
      ) : null}

      {summary.isLoading ? (
        <div className="stack"><Skel h={52} /><Skel h={52} /><Skel h={52} /></div>
      ) : summary.error ? (
        <div className="card">
          <Empty
            title="โหลดข้อมูล guardrails ไม่สำเร็จ"
            body={summary.error instanceof Error ? summary.error.message : 'กรุณาลองอีกครั้ง'}
          />
        </div>
      ) : rows.length === 0 ? (
        <div className="card">
          <Empty
            title="ยังไม่พบเอกสารที่มีความเสี่ยง"
            body="เมื่อตรวจเอกสารแล้ว ธงที่พบจะปรากฏที่นี่พร้อมตำแหน่งและตัวอย่างที่ปกปิดข้อมูลส่วนบุคคล"
          />
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>เอกสาร</th>
                <th>ประเภท</th>
                <th>สถานะ</th>
                <th>จำนวนธง</th>
                <th>ระดับสูงสุด</th>
                <th>ตรวจล่าสุด</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((doc) => {
                const open = openId === doc.document_id
                return (
                  <Fragment key={doc.document_id}>
                    <tr
                      onClick={() => setOpenId(open ? null : doc.document_id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="mono">{doc.original_name}</td>
                      <td>{kindLabel[doc.kind] ?? doc.kind}</td>
                      <td><span className="chip">{statusLabel[doc.status] ?? doc.status}</span></td>
                      <td className="tnum">{doc.flag_count}</td>
                      <td><Severity value={doc.max_severity} /></td>
                      <td className="dim">{doc.last_flagged_at ? thaiDateTime(doc.last_flagged_at) : '—'}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={rescan.isPending && rescan.variables === doc.document_id}
                          onClick={(e) => { e.stopPropagation(); rescan.mutate(doc.document_id) }}
                        >
                          {rescan.isPending && rescan.variables === doc.document_id ? 'กำลังตรวจ…' : 'ตรวจใหม่'}
                        </button>
                        <span className="dim" style={{ fontSize: 11, marginLeft: 8 }}>
                          {open ? 'ซ่อน' : 'รายละเอียด'}
                        </span>
                      </td>
                    </tr>
                    {open ? (
                      <tr>
                        <td colSpan={7} style={{ background: 'var(--color-surface-2)', padding: '14px 18px' }}>
                          <div className="eyebrow" style={{ marginBottom: 10 }}>หลักฐานประกอบธง</div>
                          {doc.flags.map((f) => (
                            <div
                              key={f.id}
                              className="card"
                              style={{ padding: 14, marginBottom: 8, background: 'var(--color-surface-1)' }}
                            >
                              <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                                <span className="chip gold">{codeMeta[f.code]?.label ?? f.code}</span>
                                <Severity value={f.severity} />
                                <span className="dim" style={{ fontSize: 11 }}>
                                  ตำแหน่งอักขระที่ {f.offset}
                                </span>
                                <span className="dim mono" style={{ fontSize: 10.5, marginLeft: 'auto' }}>{f.code}</span>
                              </div>
                              <div
                                className="mono"
                                style={{
                                  marginTop: 10,
                                  fontSize: 12,
                                  lineHeight: 1.7,
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {maskSensitive(f.excerpt)}
                              </div>
                              <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.6 }}>
                                {f.detail}
                              </div>
                            </div>
                          ))}
                          {doc.flags.length === 0 ? (
                            <p className="dim" style={{ fontSize: 12 }}>ไม่มีรายละเอียดธงสำหรับเอกสารนี้</p>
                          ) : null}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
